import {Injectable, inject} from '@angular/core';
import {catchError, Observable, throwError} from 'rxjs';
import {ApiService} from '@services/api/api.service';
import {ApiResponse} from '@interfaces/response';
import {ExplorerEntity} from '@interfaces/explorer-entity';
import {environment} from '@environment/environment';
import { LoaderService } from '../../loader/loader.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class FilemanagerApiService {


  private api_service = inject(ApiService);
  private translate = inject(TranslateService);
  private filemanager_url: string = '/filemanager/files';
  private filemanager_image_url: string = '/filemanager/image';
  private filemanager_tmp_url: string = '/filemanager/file/tmp';
  private filemanager_screenshot_url: string = '/filemanager/file/screenshot';
  private filemanager_pdf_cut_url: string = '/filemanager/pdf/page/cut';

  constructor(
    private snackBar: MatSnackBar
  ) {
  }

  // companyId: the superadmin's Documenti page shows one company at a time
  public getList(parent_id: number | null = null, ordering = '-created_at', loaderShow = true,
                 companyId: number | null = null): Observable<ApiResponse<ExplorerEntity[]>> {
    let url = `${this.filemanager_url}?parent=${parent_id}`;
    if (ordering) {
      url = `${url}&ordering=${ordering}`;
    }
    if (companyId) {
      url = `${url}&companies=${companyId}`;
    }
    // loaderShow=false è usato dal polling dello stato RAG per non mostrare il loader.
    return this.api_service.get<ApiResponse<ExplorerEntity>>(url, undefined, undefined, loaderShow);
  }

  public renameItem(id: number, newName: string): Observable<ApiResponse<ExplorerEntity>> | ExplorerEntity {
    let url = `${this.filemanager_url}/${id}`;
    const body = {name: newName};
    return this.api_service.patch<ApiResponse<ExplorerEntity>>(url, body);
  }
  public moveItem(id: number, parentId: number, newName:string): Observable<ApiResponse<ExplorerEntity>> | ExplorerEntity {
    let url = `${this.filemanager_url}/${id}`;
    const _parentId = parentId  ? parentId : null;
    const body = {name: newName,parent: _parentId};
    return this.api_service.patch<ApiResponse<ExplorerEntity>>(url, body);
  }
  public deleteItem(id: number): Observable<ApiResponse<ExplorerEntity>> | ExplorerEntity {
    let url = `${this.filemanager_url}/${id}`;
    return this.api_service.delete<ApiResponse<ExplorerEntity | null>>(url);
  }

  public uploadFile(body: ExplorerEntity): Observable<ApiResponse<ExplorerEntity>> {
    return this.api_service.post<ApiResponse<ExplorerEntity>>(this.filemanager_url, body);
  }

  public createFolder(body: any): Observable<ApiResponse<ExplorerEntity>> {
    return this.api_service.post<ApiResponse<ExplorerEntity>>(this.filemanager_url, body);
  }

  public getFile(file_id: number): Observable<ApiResponse<ExplorerEntity>> {
    let url = `${this.filemanager_url}/${file_id}`;
    return this.api_service.get<ApiResponse<ExplorerEntity>>(url);
  }

  public getFileBytes(file_uuid: string = ""): Observable<ApiResponse<ExplorerEntity>> {
    let url = `${this.filemanager_image_url}/${file_uuid}`;
    return this.api_service.get<ApiResponse<ExplorerEntity>>(url, null, {responseType: 'blob'})
    .pipe(
      catchError((err, caught) => {
        console.log(err, caught);
        this.manageApiError(err, this.translate.instant('filemanager.errors.download'));
        return throwError(() => err);
      })
    )
  }

  public getFileBytesToPromise(file_uuid: string = ""): Promise<any> {
    let url = `${this.filemanager_image_url}/${file_uuid}`;
    const prom = this.api_service.get<ApiResponse<ExplorerEntity>>(url, null, {responseType: 'blob'}).toPromise();
    prom.catch((err) => {this.manageApiError(err, this.translate.instant('filemanager.errors.download'))})
    return prom;
  }

  public manageApiError(err: any, message?: string) {
    console.log(err)
    if (err instanceof HttpErrorResponse) {
    console.error('Status:', err.status);           // Es: 404, 500
    console.error('Message:', err.message);         // Messaggio generico
    console.error('Error:', err.error);             // Corpo dell'errore (se il backend restituisce JSON)
    console.error('URL:', err.url);                 // URL che ha fallito
  } else {
    console.error('Errore sconosciuto:', err);
  }
    const text: string = message || this.translate.instant('filemanager.errors.generic');
    this.snackBar.open(text, this.translate.instant('filemanager.errors.close'), {
      duration: 5000,        // auto-hide dopo 5s
      panelClass: ['error-snackbar'] // opzionale per styling custom
    });
  }

  public saveFileTMP(base64: string): Observable<ApiResponse<any>> {
    let url = `${this.filemanager_tmp_url}`;
    let body = {file_base64: base64};
    return this.api_service.post<ApiResponse<ExplorerEntity>>(url, body);
  }


  public saveFileTmpPromise(base64: string): Promise<any> {
    let url = `${this.filemanager_tmp_url}`;
    let body = {file_base64: base64};
    return this.api_service.post<ApiResponse<ExplorerEntity>>(url, body).toPromise();
  }

  public sendToRag(file_uuid: string): Observable<ApiResponse<any>> {
    let url = `${this.filemanager_url}/${file_uuid}/rag/`;
    return this.api_service.post<ApiResponse<any>>(url, {}).pipe(
      catchError((err) => {
        this.manageApiError(err, this.translate.instant('filemanager.errors.analyze'));
        return throwError(() => err);
      })
    );
  }

  public removeFromRag(file_uuid: string): Observable<ApiResponse<any>> {
    let url = `${this.filemanager_url}/${file_uuid}/rag/`;
    return this.api_service.delete<ApiResponse<any>>(url).pipe(
      catchError((err) => {
        this.manageApiError(err, this.translate.instant('filemanager.errors.remove_knowledge'));
        return throwError(() => err);
      })
    );
  }

  public cutPagePdfPromise(uuid: string, page: number): Promise<any> {
    let url = `${this.filemanager_pdf_cut_url}`;
    let body = {uuid: uuid, page: page};
    return this.api_service.post<ApiResponse<any>>(url, body).toPromise();
  }


  public saveFileScreenshot(base64: string, filename?: string): Observable<ApiResponse<any>> {
    let url = `${this.filemanager_screenshot_url}`;
    let body: any = {file_base64: base64};
    if(filename) {
      body.filename = filename;
    }
    return this.api_service.post<ApiResponse<ExplorerEntity>>(url, body);
  }


  public saveFileScreenshotPromise(base64: string, filename?: string): Promise<any> {
    let url = `${this.filemanager_screenshot_url}`;
    let body: any = {file_base64: base64};
    if(filename) {
      body.filename = filename;
    }
    return this.api_service.post<ApiResponse<ExplorerEntity>>(url, body).toPromise();
  }
}
